import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api";
import { FALLBACK_TRIAL_TERMS } from "../content/trialTerms";
import {
  isFreePlan,
  formatPlanPrice,
  offeredPlans,
  defaultPlanId,
} from "../utils/mobilePlanCatalogue";

// Re-exported so the screens that already import them from this hook keep working. The
// definitions now live in utils/mobilePlanCatalogue, which the public site shares.
export { isFreePlan, formatPlanPrice };

/**
 * The student app-plan purchase flow: load plans, start a free trial or a Stripe checkout,
 * fulfil the session Stripe redirects back with, and open the billing portal.
 *
 * Extracted from StudentSubscriptionLock so the Billing page can offer Continue/Renew without
 * a second copy of the handshake — the fulfil step in particular is easy to get subtly wrong.
 *
 * @param {object}  options
 * @param {number}  options.preferredPlanId  Plan to preselect, from the access payload.
 * @param {string}  options.redirectTo       Where to go once a plan is active.
 * @param {boolean} options.enabled          False for accounts that cannot hold an app plan
 *                                           (course-only students), so we never call the
 *                                           endpoints that would 401 for them.
 */
const useStudentPlanCheckout = ({
  preferredPlanId: preferredFromCaller = null,
  redirectTo = "/student",
  enabled = true,
} = {}) => {
  const location = useLocation();
  const fulfilledSessionRef = useRef(null);

  const [plans, setPlans] = useState([]);
  // Trial length, card requirement and roll-over plan, as the API reports them.
  const [trialTerms, setTrialTerms] = useState(FALLBACK_TRIAL_TERMS);
  const [preferredPlanId, setPreferredPlanId] = useState(Number(preferredFromCaller || 0) || null);
  const [selectedPlanId, setSelectedPlanId] = useState(Number(preferredFromCaller || 0) || null);
  const [loadingPlans, setLoadingPlans] = useState(enabled);
  const [busy, setBusy] = useState(false);
  const [fulfilling, setFulfilling] = useState(false);
  const [error, setError] = useState("");
  // Whether this account may still start a free trial. Undefined until the API answers, which
  // offeredPlans() reads as "don't filter yet" rather than "hide it".
  const [canStartFreeTrial, setCanStartFreeTrial] = useState(undefined);
  // What this subscriber already pays. Early adopters keep their original amount, so this is
  // the figure to charge and to show — never the plan's current price.
  const [retainedPrice, setRetainedPrice] = useState(null);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  // How this user gets back onto a plan: renew the one they had, or choose afresh with a
  // floor. Decided by the API so every picker agrees. See renewalPolicyForUser().
  const [renewal, setRenewal] = useState(null);
  const recoveredRef = useRef(false);

  const selectedPlan = useMemo(
    () => plans.find((p) => Number(p.id) === Number(selectedPlanId)) || null,
    [plans, selectedPlanId]
  );

  useEffect(() => {
    if (!enabled) {
      setLoadingPlans(false);
      return undefined;
    }

    let cancelled = false;
    setLoadingPlans(true);
    setError("");

    // Renewing should be one click: someone whose plan expired starts on the plan they were
    // on, not on whatever the catalogue happens to list first.
    const pickDefaultPlanId = (rows, preferred, policy) =>
      // No Standard (or any) fallback: only the plan the user chose / is renewing.
      defaultPlanId({ plans: rows, renewal: policy, preferredPlanId: preferred }) ?? null;

    const applyRows = (rows, payload = {}) => {
      const preferred = Number(payload.preferred_plan_id || preferredFromCaller || 0) || null;
      setPreferredPlanId(preferred);

      if (payload.can_start_free_trial !== undefined) {
        setCanStartFreeTrial(Boolean(payload.can_start_free_trial));
      }
      setRetainedPrice(payload.retained_price ?? null);
      setCurrentPlanId(payload.current_plan_id ?? null);

      const policy = payload.renewal ?? null;
      setRenewal(policy);

      // Mark a free trial this account has already used, and anything below this user's
      // floor — the plan they were on after an expiry, or Monthly after a cancellation.
      // Marked rather than removed: the pickers grey those rows and print
      // `blockedReason`, because a plan that silently vanishes explains nothing.
      const visible = offeredPlans({
        plans: rows,
        renewal: policy,
        canStartFreeTrial: payload.can_start_free_trial,
        currentPlanPricePence: Number(payload.retained_price?.price_pence || 0),
      });

      setPlans(visible);
      // The preselection must land on a plan they can actually buy.
      setSelectedPlanId(
        pickDefaultPlanId(visible.filter((p) => p.selectable), preferred, policy)
      );
    };

    api
      .get("student/subscription/plans")
      .then((res) => {
        if (cancelled) return;
        if (res.data?.trial_terms) setTrialTerms(res.data.trial_terms);
        applyRows(Array.isArray(res.data?.data) ? res.data.data : [], res.data || {});
      })
      .catch(async () => {
        // Fallback if the student plans route is not deployed yet — still let them pick a plan.
        try {
          const res = await api.get("mobile/plans");
          if (cancelled) return;
          if (res.data?.trial_terms) setTrialTerms(res.data.trial_terms);
          applyRows(Array.isArray(res.data?.data) ? res.data.data : [], res.data || {});
        } catch (e) {
          if (!cancelled) {
            setError(
              e.response?.data?.message ||
                "Could not load subscription plans. Please refresh or try again shortly."
            );
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPlans(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, preferredFromCaller]);

  // Stripe Checkout returns with ?session_id=cs_...
  useEffect(() => {
    if (!enabled) return undefined;

    const sessionId = (new URLSearchParams(location.search || "").get("session_id") || "").trim();
    if (!sessionId || !sessionId.startsWith("cs_")) return undefined;
    if (fulfilledSessionRef.current === sessionId) return undefined;
    fulfilledSessionRef.current = sessionId;

    setFulfilling(true);
    setError("");
    api
      .post("student/subscription/fulfill", { session_id: sessionId })
      .then((res) => {
        if (res.data?.data?.has_resource_access === false) {
          setError(
            res.data?.message ||
              "Payment received, but access is not active yet. Refresh in a moment."
          );
          return;
        }
        toast.success("Subscription activated. Welcome back!");
        localStorage.removeItem("student-subscription-lock");
        window.location.assign(redirectTo);
      })
      .catch((e) => {
        setError(
          e.response?.data?.message ||
            "Could not confirm payment. If you were charged, refresh or contact support."
        );
      })
      .finally(() => setFulfilling(false));

    return undefined;
  }, [enabled, location.search, redirectTo]);

  /**
   * Open Stripe's billing page.
   *
   * @param {string|null} flow  "payment_method_update" drops the user straight onto the card
   *                            form. For someone whose card was declined the portal overview
   *                            is a dead end they have to navigate out of, so the API picks
   *                            this automatically when the account is in that state.
   */
  // Return trip from the Stripe card form (?billing_return=1). The user has done their one
  // interaction — saving the card — so finish the job for them: charge the retained price and
  // reactivate, without a second confirmation. The endpoint is idempotent, and the ref guards
  // against React 18 double-invoking the effect in development.
  useEffect(() => {
    if (!enabled) return undefined;

    const params = new URLSearchParams(location.search || "");
    if (params.get("billing_return") !== "1") return undefined;
    if (recoveredRef.current) return undefined;
    recoveredRef.current = true;

    setFulfilling(true);
    setError("");
    api
      .post("student/subscription/recover")
      .then((res) => {
        // Saved a card with no lapsed plan to restart (a trial that ended, or a plan they
        // cancelled): nothing went wrong — they still pick a plan below.
        if (res.data?.data?.reason === "no_subscription") {
          toast.info("Card saved. Choose a plan to continue.");
          return;
        }
        // The grace window for the original price has closed: they renew at today's price.
        if (res.data?.data?.reason === "grace_expired") {
          toast.info("Card saved. Your original price has expired — choose a plan to continue.");
          return;
        }
        if (!res.data?.data?.recovered) {
          setError(
            res.data?.message ||
              "We saved your card but could not restart your plan. Please contact support."
          );
          return;
        }
        toast.success("Your plan is active again.");
        localStorage.removeItem("student-subscription-lock");
        window.location.assign(redirectTo);
      })
      .catch((e) => {
        setError(
          e.response?.data?.message ||
            "We saved your card but could not restart your plan. Please contact support."
        );
      })
      .finally(() => setFulfilling(false));

    return undefined;
  }, [enabled, location.search, redirectTo]);

  const openBillingPortal = useCallback(async (flow = null) => {
    setBusy(true);
    setError("");
    try {
      const res = await api.post("student/subscription/billing-portal", {
        return_path: `${location.pathname}${location.search || ""}`,
        ...(flow ? { flow } : {}),
      });
      const url = res.data?.data?.portal_url;
      if (!url) {
        toast.error("Could not open the billing page. Please try again.");
        return;
      }
      window.location.assign(url);
    } catch (e) {
      setError(e.response?.data?.message || "Could not open the billing page.");
    } finally {
      setBusy(false);
    }
  }, [location.pathname, location.search]);

  const continueWithPlan = useCallback(
    async (planOverride = null) => {
      const plan = planOverride || selectedPlan;
      if (!plan) {
        setError("Please select a plan.");
        return;
      }
      setBusy(true);
      setError("");
      try {
        if (isFreePlan(plan)) {
          const res = await api.post("student/subscription/start-trial", { plan_id: plan.id });
          if (res.data?.data?.has_resource_access === false) {
            setError(res.data?.message || "Trial started, but access is still locked.");
            return;
          }
          toast.success("Free trial started!");
          localStorage.removeItem("student-subscription-lock");
          window.location.assign(redirectTo);
          return;
        }

        const res = await api.post("student/subscription/checkout", { plan_id: plan.id });
        const url = res.data?.data?.checkout_url;
        if (!url) {
          setError("Could not start checkout. Please try again.");
          return;
        }
        window.location.assign(url);
      } catch (e) {
        setError(e.response?.data?.message || "Could not continue with this plan.");
      } finally {
        setBusy(false);
      }
    },
    [selectedPlan, redirectTo]
  );

  return {
    plans,
    trialTerms,
    selectedPlan,
    selectedPlanId,
    setSelectedPlanId,
    preferredPlanId,
    loadingPlans,
    busy,
    fulfilling,
    error,
    setError,
    openBillingPortal,
    continueWithPlan,
    canStartFreeTrial,
    retainedPrice,
    currentPlanId,
    renewal,
  };
};

export default useStudentPlanCheckout;
